import * as React from 'react';
import { useSelector } from 'react-redux';

import { CartPrices } from './types';
import { Money } from '../Money/types';
import { useConfig, useIsCheckin } from '../context';
import {
	getTravellers,
	getSelectedAnimalServicesPrice,
	getSelectedGroundTransportationServicesPrice,
	getSelectedPriorityBoardingPrice,
	getSelectedSmsNotificationsPrice,
	getSelectedRefundableTicketPrice,
	getSelectedUpgradeClassPrice
} from '../Checkout/store/order/selectors';
import { getCalculatedBaggageDeliveryPrice, getPriceToPay } from '../Checkout/store/orderPrice/selectors';
import {
	getPriceToPay as checkinGetPriceToPay,
	getTravellers as checkinGetTravellers
} from '../Modules/Checkin/store/order/selectors';

export const useCartPrices = () => {
	const { servicesAvailable } = useConfig().global;
	const isCheckin = useIsCheckin();

	let priceToPay: ReturnType<typeof getPriceToPay>;
	let travellers: ReturnType<typeof getTravellers> | ReturnType<typeof checkinGetTravellers>;

	let animalSelectedPrice: ReturnType<typeof getSelectedAnimalServicesPrice>;
	let transferSelectedPrice: ReturnType<typeof getSelectedGroundTransportationServicesPrice>;
	let prioritySelectedPrice: ReturnType<typeof getSelectedPriorityBoardingPrice>;
	let baggageDeliveryPrice: ReturnType<typeof getCalculatedBaggageDeliveryPrice>;
	let upgradeSelectedPrice: ReturnType<typeof getSelectedUpgradeClassPrice>;
	let smsSelectedPrice: ReturnType<typeof getSelectedSmsNotificationsPrice>;
	let refundableTicketPrice: ReturnType<typeof getSelectedRefundableTicketPrice>;

	if (isCheckin) {
		priceToPay = useSelector(checkinGetPriceToPay);
		travellers = useSelector(checkinGetTravellers);
	} else {
		priceToPay = useSelector(getPriceToPay);
		travellers = useSelector(getTravellers);
		animalSelectedPrice = useSelector(getSelectedAnimalServicesPrice);
		transferSelectedPrice = useSelector(getSelectedGroundTransportationServicesPrice);
		prioritySelectedPrice = useSelector(getSelectedPriorityBoardingPrice);
		baggageDeliveryPrice = useSelector(getCalculatedBaggageDeliveryPrice);
		upgradeSelectedPrice = useSelector(getSelectedUpgradeClassPrice);
		smsSelectedPrice = useSelector(getSelectedSmsNotificationsPrice);
		refundableTicketPrice = useSelector(getSelectedRefundableTicketPrice);
	}

	return React.useMemo<CartPrices>(() => {
		// Baggage price calculation
		let baggagePrice: Money = { ...priceToPay.baggage };

		// [Animals]
		if (servicesAvailable.animal && animalSelectedPrice && animalSelectedPrice.amount <= baggagePrice.amount) {
			baggagePrice = { ...baggagePrice, amount: baggagePrice.amount - animalSelectedPrice.amount };
		}

		if (baggagePrice.amount === 0) {
			baggagePrice = null;
		}

		// Vip services calculation
		let vipPrice: Money = { ...priceToPay.vip };

		[
			transferSelectedPrice,
			prioritySelectedPrice,
			upgradeSelectedPrice,
			smsSelectedPrice,
			refundableTicketPrice
		].forEach(vipServicePrice => {
			if (vipServicePrice && vipServicePrice.amount <= vipPrice.amount) {
				vipPrice = { ...vipPrice, amount: vipPrice.amount - vipServicePrice.amount };
			}
		});

		if (vipPrice.amount === 0) {
			vipPrice = null;
		}

		return {
			...priceToPay,
			vip: vipPrice,
			baggage: baggagePrice,
			animals: animalSelectedPrice,
			transfer: transferSelectedPrice,
			priorityBoarding: prioritySelectedPrice,
			baggageDelivery: baggageDeliveryPrice,
			refundableTicket: refundableTicketPrice,
			upgrade: upgradeSelectedPrice,
			sms: smsSelectedPrice
		};
	}, [
		priceToPay,
		travellers,
		animalSelectedPrice,
		transferSelectedPrice,
		prioritySelectedPrice,
		smsSelectedPrice,
		refundableTicketPrice
	]);
};
